import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Mail, Gift } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SUPPRESSED_PATHS = ['/affiliate', '/affiliate/dashboard', '/admin', '/login', '/register'];

const DiscountPopup = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [code, setCode] = useState('');

  const isSuppressed = SUPPRESSED_PATHS.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    if (isSuppressed) return;
    const dismissed = localStorage.getItem('discount_popup_shown');
    if (dismissed) return;

    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 10000);

    return () => clearTimeout(timer);
  }, [isSuppressed]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('discount_popup_shown', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/promo/discount`, { email });
      setCode(data.code);
      setSuccess(true);
      localStorage.setItem('discount_popup_shown', 'true');
      toast.success('Discount code sent to your email!');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      data-testid="discount-popup"
    >
      <div className="relative max-w-md w-full mx-4 bg-[#0C0E14] border border-[#1E2532] rounded-2xl p-8 text-center">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 text-[#A1A7B5] hover:text-white transition-colors"
          data-testid="discount-popup-close"
        >
          <X className="w-5 h-5" />
        </button>

        {!success ? (
          <>
            <div className="w-16 h-16 mx-auto mb-5 bg-[#00F0FF]/10 rounded-full flex items-center justify-center">
              <Gift className="w-8 h-8 text-[#00F0FF]" />
            </div>

            <h2
              className="text-2xl font-bold text-white mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Get <span className="text-[#00F0FF]">10% Off</span>
            </h2>
            <p className="text-[#A1A7B5] text-sm mb-6">
              Enter your email and we'll send you a 10% discount code for your first order.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="input-field w-full pl-10"
                  data-testid="discount-email-input"
                />
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-3"
                data-testid="discount-submit-btn"
              >
                {loading ? 'Sending...' : 'Get My 10% Off'}
              </Button>
            </form>

            <button
              onClick={handleClose}
              className="text-[#A1A7B5] text-xs mt-4 hover:text-white transition-colors"
              data-testid="discount-no-thanks"
            >
              No thanks, I'll pay full price
            </button>
          </>
        ) : (
          <>
            <div className="w-16 h-16 mx-auto mb-5 bg-green-500/10 rounded-full flex items-center justify-center">
              <Gift className="w-8 h-8 text-green-400" />
            </div>

            <h2
              className="text-2xl font-bold text-white mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Check Your Email!
            </h2>
            <p className="text-[#A1A7B5] text-sm mb-4">
              We've sent your 10% discount code to <span className="text-white">{email}</span>
            </p>

            <div className="bg-[#111621] border border-dashed border-[#00F0FF] rounded-lg p-4 mb-6">
              <p className="text-[#A1A7B5] text-xs mb-1">Your code:</p>
              <p className="text-[#00F0FF] text-2xl font-bold tracking-widest" data-testid="discount-code-display">
                {code}
              </p>
            </div>

            <Button
              onClick={handleClose}
              className="btn-primary w-full py-3"
              data-testid="discount-continue-btn"
            >
              Continue Shopping
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default DiscountPopup;
